"use client";

import { useT } from "@/hooks/useT";

export type WeatherTempType = "high" | "low" | null;

export interface WeatherDateTab {
  date: string;
  count: number;
}

interface WeatherChipsRowProps {
  category: string;
  activeSubcategory: string | null;
  weatherDateTabs: WeatherDateTab[];
  activeWeatherDate: string | null;
  setActiveWeatherDate: (date: string | null) => void;
  activeWeatherTempType: WeatherTempType;
  setActiveWeatherTempType: (t: WeatherTempType) => void;
  /** "sm" for the mobile compact toolbar, "md" for the desktop row. */
  size?: "sm" | "md";
}

/**
 * High/Low temperature chips + per-date chips for the weather category.
 *
 * The two original inline blocks (mobile + desktop) shared the entire body;
 * the only differences were padding/text-size and the container's gap.
 * Collapsing them with a `size` switch keeps the markup honest without
 * an extra wrapper level. Visibility (category guard, subcategory guard)
 * stays in the parent's surrounding fragment.
 */
export function WeatherChipsRow({
  category,
  activeSubcategory,
  weatherDateTabs,
  activeWeatherDate,
  setActiveWeatherDate,
  activeWeatherTempType,
  setActiveWeatherTempType,
  size = "md",
}: WeatherChipsRowProps) {
  const t = useT();

  const isWeather = category === "weather" || category === "weather-science";
  const shouldRender = isWeather && (activeSubcategory === "temperature" || weatherDateTabs.length > 0);
  if (!shouldRender) return null;

  const containerCls =
    size === "sm" ? "flex items-center gap-1 flex-shrink-0 mt-2" : "flex items-center gap-1.5 mt-2";
  const btnCls = size === "sm" ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs";

  return (
    <div className={containerCls}>
      {activeSubcategory === "temperature" &&
        (["high", "low"] as const).map((tt) => (
          <button
            key={tt}
            type="button"
            onClick={() => setActiveWeatherTempType(activeWeatherTempType === tt ? null : tt)}
            className={`${btnCls} rounded-[5px] font-semibold transition-colors whitespace-nowrap ${
              activeWeatherTempType === tt
                ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
            }`}
          >
            {t(tt === "high" ? "weather.tempHigh" : "weather.tempLow")}
          </button>
        ))}
      {weatherDateTabs.map((dt) => {
        const d = new Date(dt.date + "T12:00:00Z");
        const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        return (
          <button
            key={dt.date}
            type="button"
            onClick={() => setActiveWeatherDate(activeWeatherDate === dt.date ? null : dt.date)}
            className={`${btnCls} rounded-[5px] font-semibold transition-colors whitespace-nowrap ${
              activeWeatherDate === dt.date
                ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
